/**
 * Normalise les compteurs de hits (surtout IA) renvoyés par le VPS / cracker.
 * Les backends utilisent souvent des casing / alias différents
 * (openaiHits vs openAiHits), et dumpent les IA dans otherHits.
 */

export type ScanStatsLike = Record<string, unknown>;

const AI_STAT_ALIASES: Record<string, string[]> = {
  openAiHits: [
    'openAiHits',
    'openaiHits',
    'OpenAIHits',
    'OpenaiHits',
    'open_ai_hits',
    'openai_hits',
    'gptHits',
    'GPTHits',
  ],
  anthropicHits: [
    'anthropicHits',
    'AnthropicHits',
    'anthropic_hits',
    'claudeHits',
    'ClaudeHits',
  ],
  googleAiHits: [
    'googleAiHits',
    'googleAIHits',
    'GoogleAIHits',
    'google_ai_hits',
    'geminiHits',
    'GeminiHits',
  ],
  huggingFaceHits: [
    'huggingFaceHits',
    'huggingfaceHits',
    'HuggingFaceHits',
    'hugging_face_hits',
    'hfHits',
  ],
  groqHits: ['groqHits', 'GroqHits', 'groq_hits'],
  cohereHits: ['cohereHits', 'CohereHits', 'cohere_hits'],
  mistralHits: ['mistralHits', 'MistralHits', 'mistral_hits'],
};

/** Tous les compteurs de services affichés (hors Autres) */
const ALL_SERVICE_STAT_ALIASES: Record<string, string[]> = {
  awsHits: ['awsHits', 'AwsHits', 'AWSHits', 'aws_hits'],
  sendGridHits: ['sendGridHits', 'sendgridHits', 'SendGridHits', 'send_grid_hits'],
  sparkpostHits: ['sparkpostHits', 'SparkpostHits', 'sparkPostHits', 'spark_post_hits'],
  twilioHits: ['twilioHits', 'TwilioHits', 'twilio_hits'],
  brevoHits: ['brevoHits', 'BrevoHits', 'brevo_hits', 'sendinblueHits', 'SendinblueHits'],
  smtpHits: ['smtpHits', 'SmtpHits', 'SMTPHits', 'smtp_hits'],
  mailgunHits: ['mailgunHits', 'MailgunHits', 'MailGunHits', 'mail_gun_hits'],
  mailchimpHits: ['mailchimpHits', 'MailchimpHits', 'mail_chimp_hits'],
  postmarkHits: ['postmarkHits', 'PostmarkHits', 'post_mark_hits'],
  stripeHits: ['stripeHits', 'StripeHits', 'stripe_hits'],
  ...AI_STAT_ALIASES,
};

const SERVICE_MAP_KEYS = [
  'hitsByService',
  'serviceHits',
  'byService',
  'servicesHits',
  'hitCounts',
  'countsByService',
  'HitsByService',
];

const SERVICE_LABEL_TO_STAT: Record<string, string> = {
  aws: 'awsHits',
  amazon: 'awsHits',
  sendgrid: 'sendGridHits',
  'send grid': 'sendGridHits',
  sparkpost: 'sparkpostHits',
  'spark post': 'sparkpostHits',
  twilio: 'twilioHits',
  brevo: 'brevoHits',
  sendinblue: 'brevoHits',
  'send in blue': 'brevoHits',
  smtp: 'smtpHits',
  mailgun: 'mailgunHits',
  'mail gun': 'mailgunHits',
  mailchimp: 'mailchimpHits',
  'mail chimp': 'mailchimpHits',
  postmark: 'postmarkHits',
  'post mark': 'postmarkHits',
  stripe: 'stripeHits',
  openai: 'openAiHits',
  'open ai': 'openAiHits',
  'open-ai': 'openAiHits',
  gpt: 'openAiHits',
  anthropic: 'anthropicHits',
  claude: 'anthropicHits',
  'google ai': 'googleAiHits',
  googleai: 'googleAiHits',
  'google-ai': 'googleAiHits',
  gemini: 'googleAiHits',
  huggingface: 'huggingFaceHits',
  'hugging face': 'huggingFaceHits',
  'hugging-face': 'huggingFaceHits',
  hf: 'huggingFaceHits',
  groq: 'groqHits',
  cohere: 'cohereHits',
  mistral: 'mistralHits',
};

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
};

const KEY_PATTERN_TO_STAT: Array<{ pattern: RegExp; stat: string }> = [
  { pattern: /^aws/i, stat: 'awsHits' },
  { pattern: /sendgrid/i, stat: 'sendGridHits' },
  { pattern: /sparkpost/i, stat: 'sparkpostHits' },
  { pattern: /twilio/i, stat: 'twilioHits' },
  { pattern: /brevo|sendinblue/i, stat: 'brevoHits' },
  { pattern: /^smtp/i, stat: 'smtpHits' },
  { pattern: /mailgun/i, stat: 'mailgunHits' },
  { pattern: /mailchimp/i, stat: 'mailchimpHits' },
  { pattern: /postmark/i, stat: 'postmarkHits' },
  { pattern: /stripe/i, stat: 'stripeHits' },
  { pattern: /openai|open_ai|gpt/i, stat: 'openAiHits' },
  { pattern: /anthropic|claude/i, stat: 'anthropicHits' },
  { pattern: /google.?ai|gemini/i, stat: 'googleAiHits' },
  { pattern: /hugging.?face|\bhf\b/i, stat: 'huggingFaceHits' },
  { pattern: /groq/i, stat: 'groqHits' },
  { pattern: /cohere/i, stat: 'cohereHits' },
  { pattern: /mistral/i, stat: 'mistralHits' },
];

const pickAliased = (raw: ScanStatsLike, aliases: string[]): number => {
  for (const key of aliases) {
    const n = toNumber(raw[key]);
    if (n !== undefined) return n;
  }
  return 0;
};

const scanKeysForServiceCounts = (raw: ScanStatsLike): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (/other/i.test(key)) continue;
    const n = toNumber(value);
    if (n === undefined || n <= 0) continue;
    for (const { pattern, stat } of KEY_PATTERN_TO_STAT) {
      if (pattern.test(key)) {
        out[stat] = Math.max(out[stat] ?? 0, n);
        break;
      }
    }
  }

  for (const nestKey of ['ai', 'AI', 'llm', 'LLM', 'aiHits', 'AIHits', 'services', 'Services']) {
    const nest = raw[nestKey];
    if (!nest || typeof nest !== 'object' || Array.isArray(nest)) continue;
    for (const [key, value] of Object.entries(nest as Record<string, unknown>)) {
      const n = toNumber(value);
      if (n === undefined || n <= 0) continue;
      for (const { pattern, stat } of KEY_PATTERN_TO_STAT) {
        if (pattern.test(key)) {
          out[stat] = Math.max(out[stat] ?? 0, n);
          break;
        }
      }
    }
  }

  return out;
};

/** Split camelCase / snake / kebab into comparable service labels */
export const normalizeServiceKey = (key: string): string =>
  key
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const extractServiceMap = (raw: ScanStatsLike): Record<string, number> | null => {
  for (const key of SERVICE_MAP_KEYS) {
    const candidate = raw[key];
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      return candidate as Record<string, number>;
    }
  }
  return null;
};

const countsFromServiceMap = (map: Record<string, number>): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const [rawKey, rawVal] of Object.entries(map)) {
    const n = toNumber(rawVal);
    if (n === undefined || n <= 0) continue;
    const statKey = SERVICE_LABEL_TO_STAT[normalizeServiceKey(rawKey)];
    if (!statKey) continue;
    out[statKey] = (out[statKey] ?? 0) + n;
  }
  return out;
};

export type HitLikeForStats = {
  services?: string[];
  credentials?: Record<string, unknown>;
};

/** Compte les hits par service à partir des objets hit dashboard */
export const countServiceHitsFromHitList = (hits: HitLikeForStats[]): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const hit of hits) {
    const labels = new Set<string>();
    (hit.services || []).forEach((s) => {
      if (s) labels.add(s);
    });
    Object.keys(hit.credentials || {}).forEach((k) => {
      if (k) labels.add(k);
    });

    const seenStats = new Set<string>();
    Array.from(labels).forEach((label) => {
      const statKey = SERVICE_LABEL_TO_STAT[normalizeServiceKey(label)];
      if (!statKey || seenStats.has(statKey)) return;
      seenStats.add(statKey);
      out[statKey] = (out[statKey] ?? 0) + 1;
    });
  }
  return out;
};

const AI_STAT_KEYS = Object.keys(AI_STAT_ALIASES);

/** Applique les alias / maps / hits pour remplir les compteurs et corriger otherHits */
export const normalizeScanStats = <T extends ScanStatsLike>(raw: T | null | undefined): T | null => {
  if (!raw || typeof raw !== 'object') return raw ?? null;

  const stats = { ...raw } as ScanStatsLike;
  const fromMap = extractServiceMap(stats);
  const mapCounts = fromMap ? countsFromServiceMap(fromMap) : {};
  const keyCounts = scanKeysForServiceCounts(stats);

  let hitListCounts: Record<string, number> = {};
  const maybeHits = stats.hitsList ?? stats.enrichedHits ?? stats.dashboardHits;
  if (Array.isArray(maybeHits)) {
    hitListCounts = countServiceHitsFromHitList(maybeHits as HitLikeForStats[]);
  }

  let redistributed = 0;

  (Object.keys(ALL_SERVICE_STAT_ALIASES) as string[]).forEach((statKey) => {
    const aliases = ALL_SERVICE_STAT_ALIASES[statKey];
    const previous = toNumber(stats[statKey]) ?? 0;
    const aliased = pickAliased(stats, aliases);
    const mapped = mapCounts[statKey] ?? 0;
    const scanned = keyCounts[statKey] ?? 0;
    const fromHits = hitListCounts[statKey] ?? 0;
    const next = Math.max(previous, aliased, mapped, scanned, fromHits);
    if (next > previous) {
      // Ne redistribuer depuis otherHits que pour les IA (souvent dumpées dans Autres)
      if (AI_STAT_KEYS.includes(statKey)) {
        redistributed += next - previous;
      }
    }
    stats[statKey] = next;
  });

  if (redistributed > 0) {
    const other = toNumber(stats.otherHits) ?? 0;
    stats.otherHits = Math.max(0, other - redistributed);
  }

  // Si la somme des buckets connus dépasse le total hits, clamp otherHits
  const totalHits = toNumber(stats.hits) ?? 0;
  if (totalHits > 0) {
    let known = 0;
    for (const key of Object.keys(ALL_SERVICE_STAT_ALIASES)) {
      // sendinblue est alias de brevo — éviter double compte via sendinblueHits séparé
      if (key === 'sendinblueHits' || key === 'mailchimpHits') continue;
      known += toNumber(stats[key]) ?? 0;
    }
    // mailchimp hors UI principale
    known += toNumber(stats.mailchimpHits) ?? 0;
    const other = toNumber(stats.otherHits) ?? 0;
    if (known + other > totalHits) {
      stats.otherHits = Math.max(0, totalHits - known);
    }
  }

  return stats as T;
};
